// pages/CallsPage.tsx
import { useState, useMemo } from 'react';
import { Search, Filter, X, Phone, Eye, PhoneCall, PhoneOff, Mic, MicOff } from 'lucide-react';
import CallList from '../../../components/features/calls/CallsList';
import CallDetailsModal from '../../../components/features/calls/CallDetailsModal';
import CallSaveModal from '../../../components/features/calls/CallSaveModal';
import TicketSaveModal from '../../../components/features/tickets/TicketSaveModal';
import MenuModal, { Menu } from '../../../components/ui/MenuModal';
import useCalls from '../../../hooks/features/calls/useCalls';
import { Call } from '../../../types/features/Call';
import PageListLayout from '../../../layouts/PageListLayout';
import { alertSuccess, alertError } from '../../../helpers/alertError';
import { formatPhoneNumber } from '../../../utils/formatPhonenumber';
import { useSoftphoneContext } from '../../../contexts/SoftphoneContext';
import { useNavigate } from 'react-router-dom';
import useAuth from '../../../hooks/features/auth/useAuth';

export default function CallsPage() {
  const { calls, loading, pagination, stats: apiStats, fetchCalls } = useCalls();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDirection, setSelectedDirection] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate()
  const [selectedCall, setSelectedCall] = useState<Call | null>(null);
  const [callToView, setCallToView] = useState<Call | null>(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [callSaveModalOpen, setCallSaveModalOpen] = useState(false);
  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  const [preselectedClientId, setPreselectedClientId] = useState<string | undefined>(undefined);
  const {user} = useAuth()
  const {
    deviceStatus,
    callState,
    outboundNumber,
    showSoftphonePanel,
    isMuted,
    setOutboundNumber,
    setShowSoftphonePanel,
    handleMakeCall,
    acceptCall,
    hangUpCall,
    toggleMute,
  } = useSoftphoneContext();

  const filteredCalls = useMemo(() => {
    let result = calls;

    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(c =>
        c.client_name?.toLowerCase().includes(searchLower) ||
        c.client_phone?.includes(searchTerm) ||
        c.tags?.some(tag => tag.toLowerCase().includes(searchLower))
      );
    }

    if (selectedDirection) {
      result = result.filter(c => c.direction === selectedDirection);
    }

    return result;
  }, [calls, searchTerm, selectedDirection]);

  const stats = useMemo(() => {
    if (apiStats) {
      return {
        total: apiStats.total ?? pagination.total,
        entrants: apiStats.entrants ?? apiStats.entrant ?? apiStats.incoming ?? calls.filter(c => c.direction === 'entrant').length,
        sortants: apiStats.sortants ?? apiStats.sortant ?? apiStats.outgoing ?? calls.filter(c => c.direction === 'sortant').length,
        duree_moyenne: apiStats.duree_moyenne ?? 0,
      };
    }

    return {
      total: pagination.total,
      entrants: calls.filter(c => c.direction === 'entrant').length,
      sortants: calls.filter(c => c.direction === 'sortant').length,
      duree_moyenne: 0
    };
  }, [apiStats, pagination.total, calls]);

  const clearFilters = () => {
    setSelectedDirection('');
    setSearchTerm('');
  };

  const hasActiveFilters = selectedDirection !== '' || searchTerm.trim() !== '';

  const getActionsMenu = (call: Call): Menu[] => {
    return [
      {
        label: 'Voir détails',
        icon: Eye,
        onClick: () => {
         navigate(`/${user?.role}/calls/${call.id}`)
        }
      },
      {
        label: 'Rappeler (WebRTC)',
        icon: Phone,
        onClick: () => {
          if (call.client_phone) {
            const formattedNumber = formatPhoneNumber(call.client_phone);
            handleMakeCall(formattedNumber);
          } else {
            alertError(`Aucun numéro disponible pour ce client`);
          }
          setSelectedCall(null);
        }
      }
    ];
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchCalls(page, pagination.perPage);
  };

  const handleCallSaved = () => {
    setCallSaveModalOpen(false);
    fetchCalls(currentPage, pagination.perPage);
    alertSuccess('Appel enregistré avec succès');
  };

  return (
    <PageListLayout title=''>
      <div className="px-6 py-5 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-[#031B4E] tracking-tight">Appels</h1>
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-slate-100 rounded-full text-xs font-medium">
                  <span className={`w-2 h-2 rounded-full ${
                    deviceStatus === 'registered' ? 'bg-emerald-500 animate-pulse' :
                    deviceStatus === 'connecting' ? 'bg-amber-500 animate-ping' : 'bg-red-500'
                  }`} />
                  <span className="text-slate-600 capitalize">
                    {deviceStatus === 'registered' ? 'Softphone Prêt' :
                      deviceStatus === 'connecting' ? 'Connexion...' : 'Non connecté'}
                  </span>
                </div>
              </div>
              <p className="text-sm text-slate-400 font-light mt-0.5">
                Historique et suivi des communications de votre centre de relation client.
              </p>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowSoftphonePanel(!showSoftphonePanel)}
                className="flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-50 text-blue-700 font-medium text-sm rounded-xl hover:bg-blue-100 focus:outline-none transition-all"
              >
                <PhoneCall size={16} />
                {showSoftphonePanel ? 'Masquer le clavier' : 'Ouvrir le Softphone'}
              </button>

              <button
                onClick={() => setCallSaveModalOpen(true)}
                className="flex items-center justify-center gap-2 px-4 py-2.5 bg-[#031B4E] text-white font-medium text-sm rounded-xl hover:bg-[#052975] focus:outline-none focus:ring-4 focus:ring-blue-600/10 shadow-sm transition-all"
              >
                <Phone size={16} />
                Enregistrer un appel
              </button>
            </div>
          </div>

          {showSoftphonePanel && (
            <div className="mt-6 bg-slate-900 text-white p-5 rounded-2xl shadow-xl flex flex-col sm:flex-row items-center justify-between gap-4 animate-in fade-in-50 duration-200">
              <div className="flex items-center gap-3 w-full sm:w-auto">
                <div className="p-3 bg-blue-600/20 text-blue-400 rounded-xl">
                  <PhoneCall size={22} />
                </div>
                <div>
                  <h4 className="text-sm font-semibold">Centre d'appels Izi Call center</h4>
                  <p className="text-xs text-slate-400">
                    {callState === 'idle' && "Prêt à émettre ou recevoir un appel"}
                    {callState === 'ringing' && "Sonnerie en cours..."}
                    {callState === 'in-call' && "Communication établie"}
                  </p>
                </div>
              </div>

              {callState === 'idle' ? (
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <input
                    type="text"
                    placeholder="Entrer un numéro (ex: +229 90 00 00 00)"
                    value={outboundNumber}
                    onChange={(e) => setOutboundNumber(e.target.value)}
                    onBlur={(e) => {
                      if (e.target.value) {
                        const formatted = formatPhoneNumber(e.target.value);
                        setOutboundNumber(formatted);
                      }
                    }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && outboundNumber.trim()) {
                        handleMakeCall(outboundNumber);
                      }
                    }}
                    className="px-3.5 py-2 bg-slate-800 border border-slate-700 rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 w-full sm:w-64"
                  />
                  <button
                    onClick={() => handleMakeCall(outboundNumber)}
                    disabled={deviceStatus !== 'registered' || !outboundNumber.trim()}
                    className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-xl hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shrink-0"
                  >
                    <Phone size={15} /> Appeler
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
                  {callState === 'ringing' && (
                    <button
                      onClick={acceptCall}
                      className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-xl hover:bg-emerald-700 transition-all"
                    >
                      <Phone size={15} /> Décrocher
                    </button>
                  )}

                  {callState === 'in-call' && (
                    <button
                      onClick={toggleMute}
                      className={`p-2.5 rounded-xl text-sm font-medium transition-all ${
                        isMuted ? 'bg-amber-600 text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                      }`}
                      title={isMuted ? 'Activer le micro' : 'Couper le micro'}
                    >
                      {isMuted ? <MicOff size={18} /> : <Mic size={18} />}
                    </button>
                  )}

                  <button
                    onClick={hangUpCall}
                    className="flex items-center gap-1.5 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-xl hover:bg-red-700 transition-all"
                  >
                    <PhoneOff size={15} /> Raccrocher
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
            <div className="bg-slate-50 border border-slate-100 rounded-xl p-4 transition-all">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Total</p>
              <p className="text-2xl font-bold text-[#031B4E] mt-1">{stats.total}</p>
            </div>
            <div className="bg-blue-50/40 border border-blue-100/50 rounded-xl p-4 transition-all">
              <p className="text-xs font-semibold text-blue-700 uppercase tracking-wider">Appels entrants</p>
              <p className="text-2xl font-bold text-blue-900 mt-1">{stats.entrants}</p>
            </div>
            <div className="bg-slate-50 border border-slate-100 rounded-xl p-4 transition-all">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Appels sortants</p>
              <p className="text-2xl font-bold text-slate-800 mt-1">{stats.sortants}</p>
            </div>
            <div className="bg-slate-50 border border-slate-100 rounded-xl p-4 transition-all">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Durée moyenne</p>
              <p className="text-2xl font-bold text-slate-800 mt-1">{stats.duree_moyenne} min</p>
            </div>
          </div>

          <div className="relative mt-6">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Rechercher par client, numéro ou tag..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-slate-50/50 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder-slate-400 focus:border-blue-600 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-600/5 transition-all"
            />
          </div>

          <div className="mt-4">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                showFilters ? 'bg-[#031B4E] text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              <Filter size={16} /> Filtrer
              {hasActiveFilters && <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />}
            </button>

            {showFilters && (
              <div className="bg-slate-50 border border-slate-200/60 rounded-xl p-4 mt-3 space-y-4 animate-in fade-in-50 duration-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider">Filtres de recherche</h3>
                  {hasActiveFilters && (
                    <button onClick={clearFilters} className="text-xs font-medium text-red-600 hover:text-red-700 flex items-center gap-1 transition-colors">
                      <X size={14} /> Effacer tout
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide">Direction</label>
                    <select
                      value={selectedDirection}
                      onChange={(e) => setSelectedDirection(e.target.value)}
                      className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 focus:border-blue-600 focus:outline-none focus:ring-4 focus:ring-blue-600/5 transition-all"
                    >
                      <option value="">Tous les types</option>
                      <option value="entrant">Appel entrant</option>
                      <option value="sortant">Appel sortant</option>
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="px-6 py-8 max-w-7xl mx-auto">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-7 w-7 border-2 border-[#031B4E] border-t-transparent"></div>
          </div>
        ) : (
          <>
            <CallList
              calls={filteredCalls}
              onAction={(call) => setSelectedCall(call)}
              selectable={false}
            />

            {pagination.total > 0 && (
              <div className="flex justify-between items-center mt-6 pt-4 border-t border-slate-100">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 text-xs font-semibold text-slate-600 border border-slate-200 bg-white rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50 hover:text-slate-900 transition-all"
                >
                  Précédent
                </button>
                <span className="text-xs font-medium text-slate-500">
                  Page {currentPage} sur {pagination.lastPage} ({pagination.total} appels)
                </span>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === pagination.lastPage}
                  className="px-3 py-1.5 text-xs font-semibold text-slate-600 border border-slate-200 bg-white rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50 hover:text-slate-900 transition-all"
                >
                  Suivant
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {selectedCall && (
        <MenuModal
          title={`Appel - ${selectedCall.client?.full_name || 'Inconnu'}`}
          isOpen={true}
          onClose={() => setSelectedCall(null)}
          icon={null}
          menu={getActionsMenu(selectedCall)}
        />
      )}

      {callToView && (
        <CallDetailsModal
          isOpen={detailsModalOpen}
          onClose={() => {
            setDetailsModalOpen(false);
            setCallToView(null);
          }}
          call={callToView}
        />
      )}

      <CallSaveModal
        isOpen={callSaveModalOpen}
        onClose={() => setCallSaveModalOpen(false)}
        onSuccess={handleCallSaved}
      />

      <TicketSaveModal
        isOpen={ticketModalOpen}
        onClose={() => {
          setTicketModalOpen(false);
          setPreselectedClientId(undefined);
        }}
        onSuccess={() => {
          setTicketModalOpen(false);
          alertSuccess('Ticket créé avec succès depuis l\'appel');
          setPreselectedClientId(undefined);
        }}
        initialData={preselectedClientId ? { client_id: preselectedClientId } as any : undefined}
      />
    </PageListLayout>
  );
}